import type { DialogStep, EventResult, EventStep, TaskInfo, TaskStep } from '~/types/interfaces'
import { EVENT_OPERATIONS } from '~/types/constants'

// #791 MR6: task step polling の設定 (Fable Round 5 Q17)。
// - 1s 固定 interval (backoff せず、儀式 UX ラベル遷移を滑らかに)
// - 60s で "時間がかかっています…" の soft warning を dialog store 経由で挟む
// - 90s で timeout エラー
// SFN の 10min は client に引き継がない (異常時は CW Alarm が鳴っているはず)。
const TASK_POLL_INTERVAL_MS = 1000
const TASK_SLOW_WARNING_MS = 60_000
const TASK_TIMEOUT_MS = 90_000

// terminal state 判定 (Fable Round 2 Q6: publish_event_failed は成功扱い)。
const TERMINAL_STATES = new Set(['succeeded', 'publish_event_failed', 'failed'])

export function useEventSystem() {
  const store = useEventSystemStore()
  const dialogStore = useDialogStore()
  const api = useApi()
  const { $sound } = useNuxtApp()
  const { trackWonderClear, trackAreaClear } = useFunnelEvent()
  const { getSceneInfo } = useGameStateStore()

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

  // #791 MR6: task step を polling して terminal state に達したら onSuccess / onFailure
  // DialogStep を返す。ページ離脱で cleanup 済みなら AbortError で早期終了。
  const pollTask = async (
    taskId: string,
    abortSignal: AbortSignal,
  ): Promise<{ terminal: TaskInfo, warning?: string }> => {
    const startedAt = Date.now()
    let slowWarningEmitted = false
    let lastLabel = ''

    while (!abortSignal.aborted) {
      let info: TaskInfo | null = null
      try {
        info = (await api.getTask(taskId)) as TaskInfo
      }
      catch (err) {
        // network flake / 一時的な 5xx は次回 polling で拾い直す。404 が続いたら timeout
        // で拾われる (Fable Round 5 見落とし 2: resume はスコープ外、期限内に完了 or timeout)。
        console.warn('[useEventSystem] getTask failed, retrying', err)
      }

      if (info) {
        if (info.ceremonyLabel && info.ceremonyLabel !== lastLabel) {
          // 儀式 UX の label を server driven に更新 (window.__eventSystem.taskLabel で
          // dialog 側が subscribe できる)。dialog 側 (CrystallizeDialog.vue) が
          // イベントバスで受け取る想定。
          store.setTaskLabel(info.ceremonyLabel)
          lastLabel = info.ceremonyLabel
        }
        if (TERMINAL_STATES.has(info.state)) {
          return { terminal: info }
        }
      }

      const elapsed = Date.now() - startedAt
      if (elapsed >= TASK_TIMEOUT_MS) {
        throw new Error(`task polling timeout: ${taskId}`)
      }
      if (!slowWarningEmitted && elapsed >= TASK_SLOW_WARNING_MS) {
        store.setTaskLabel('時間がかかっています。もうしばらくお待ちください…')
        slowWarningEmitted = true
      }
      await sleep(TASK_POLL_INTERVAL_MS)
    }
    throw new DOMException('task polling aborted', 'AbortError')
  }

  // #791 hotfix: task step が terminal に達した瞬間を呼び出し元に知らせるフック。
  // sfn mode の cradle は「クリスタル化ダイアログを polling 中は開いたままにして
  // 儀式ラベル遷移を見せ、terminal 検知直後に閉じてから onSuccess ダイアログを開く」
  // 順を保証したい。inline mode でも呼ばれるが、その場合 polling は事実上ゼロで
  // 副作用は同じ (dialog を閉じ → follow-up dialog が出る) なので分岐不要。
  interface ProcessOptions {
    onTaskTerminated?: () => void
  }

  const processSingleEvent = async (
    result: EventResult,
    abortSignal: AbortSignal,
    options: ProcessOptions = {},
  ): Promise<void> => {
    let lastResult: string | undefined = undefined
    // #791 MR6: task step の onSuccess / onFailure を先頭に差し込むための work queue。
    // ES2015 の Array を deque として使う (先頭 shift 遅いが 1 step 単位なので無視できる)。
    const pending: EventStep[] = [...result.steps]

    while (pending.length > 0) {
      const step = pending.shift()!
      const only = step.only || ['submit']
      if (!!lastResult && !only.includes(lastResult)) {
        break
      }

      if (step.sound) {
        $sound.playSound(step.sound)
      }

      switch (step.type) {
        case 'dialog':
          lastResult = await dialogStore.showDialogAsync('event', { params: step })
          break
        case 'navigate':
          await navigateTo(step.target)
          break
        case 'task': {
          // #791 MR6: 非同期タスク polling。terminal state 検知後、onSuccess/onFailure
          // DialogStep を pending queue の **先頭** に差し込む (Fable Round 5 見落とし 2:
          // "task → 完了ダイアログ" の順を保証、将来の "task 完了後に別 step 続ける"
          // 拡張と queue 順序の関係を今のうちに決める)。
          const taskStep = step as TaskStep
          try {
            const { terminal } = await pollTask(taskStep.taskId, abortSignal)
            store.clearTaskLabel()
            // #791 hotfix: 呼び出し元にダイアログ閉鎖の機会を与えてから follow-up を積む。
            // 順序が逆だと onSuccess が開いた上に 元ダイアログを閉じることになり
            // 一瞬 2 枚が重なる (#787 と同じ症状の対称)。
            options.onTaskTerminated?.()
            const followUp = terminal.state === 'failed' ? terminal.onFailure : terminal.onSuccess
            if (followUp) {
              pending.unshift(followUp as DialogStep)
            }
            // terminal.state === 'succeeded_with_notification_failure' でも onSuccess を出す
            // (Fable Round 2 Q6: Work は存在するので client は成功扱い)。
          }
          catch (err) {
            store.clearTaskLabel()
            // fallback ダイアログを出す前にも同じフックを呼ぶ (呼び出し元は
            // 「polling が終わった」だけを知ればよく、成功/失敗の分岐は要らない)。
            options.onTaskTerminated?.()
            const message = err instanceof Error ? err.message : String(err)
            console.error('[useEventSystem] task polling failed', message)
            // timeout / abort: 汎用エラーダイアログを出す (server 側で回復済みかもしれないが、
            // client は諦める。resume はスコープ外)。
            const fallback: DialogStep = {
              type: 'dialog',
              speaker: 'magician',
              title: 'クリスタル化に失敗！',
              text: '時間内にクリスタルが仕上がらなかったみたい。もう一度試してみて。',
              actions: { cancel: { label: '閉じる', sound: 'cancel', disabled: false } },
            }
            pending.unshift(fallback)
          }
          break
        }
        case 'custom':
          switch (step.operation || EVENT_OPERATIONS.NOP) {
            case EVENT_OPERATIONS.OPEN_NAME_CHANGE_DIALOG: {
              lastResult = await dialogStore.showDialogAsync('name')
              break
            }
            case EVENT_OPERATIONS.OPEN_FAIRY_BROKER_DIALOG: {
              lastResult = await dialogStore.showDialogAsync('broker')
              break
            }
            case EVENT_OPERATIONS.OPEN_KEY_INPUT_DIALOG: {
              lastResult = await dialogStore.showDialogAsync('key', { sceneId: step.sceneId })
              break
            }
            case EVENT_OPERATIONS.OPEN_GIFT_KEY_INPUT_DIALOG: {
              lastResult = await dialogStore.showDialogAsync('key', { keyMode: 'gift' })
              break
            }
            case EVENT_OPERATIONS.OPEN_REMIX_SOURCE_DIALOG:
              lastResult = await dialogStore.showDialogAsync('remixSource')
              break
            case EVENT_OPERATIONS.OPEN_CRYSTALLIZE_PREVIEW_DIALOG:
              lastResult = await dialogStore.showDialogAsync('crystallizePreview')
              break
            case EVENT_OPERATIONS.OPEN_CRYSTALLIZE_DIALOG:
              lastResult = await dialogStore.showDialogAsync('crystallize')
              break
            case EVENT_OPERATIONS.PLAY_GODDESS_ORACLE: {
              const { playMusic } = useMusic()
              playMusic('goddessFirstOracle')
              break
            }
            case EVENT_OPERATIONS.PLAY_ENCODIA: {
              const { playMusic } = useMusic()
              playMusic('wld_encodia')
              break
            }
            case EVENT_OPERATIONS.PLAY_CODEX_IN_YOUR_HANDS: {
              const { playMusic } = useMusic()
              playMusic('codexInYourHands')
              break
            }
            case EVENT_OPERATIONS.PLAY_NONE: {
              const { stopMusic } = useMusic()
              stopMusic()
              break
            }
            case EVENT_OPERATIONS.NOP:
              // flag_updates だけが目的の正規 step。BaseStep.flag_updates は
              // backend が processSingleEvent とは独立に適用するため、フロント
              // 側はここで何もしない。ENTER:sys_editor / ENTER:cmp_* /
              // ENTER:wld_* など多くの遷移 event が該当 (#665)。
              break
            default:
              console.warn('Unknown custom operation:', step.operation)
          }
          break
        default:
          console.warn('Unknown step type:', (step as EventStep).type)
      }
      await sleep(50)
    }
  }

  // 全 step の flagUpdates を走査し、新たに <scene_id>:CLEARED が立った
  // wonder/area を GA4 に通知する。area (wld_*) は trackAreaClear、それ以外は
  // trackWonderClear (parent area 付き) として記録。
  const emitClearEvents = (steps: EventStep[]) => {
    for (const step of steps) {
      if (!step.flagUpdates) continue
      for (const [flag, value] of Object.entries(step.flagUpdates)) {
        if (value !== true || !flag.endsWith(':CLEARED')) continue
        const sceneId = flag.slice(0, -':CLEARED'.length)
        if (sceneId.startsWith('wld_')) {
          trackAreaClear(sceneId)
        }
        else {
          const parentId = getSceneInfo(sceneId).parentId ?? ''
          trackWonderClear(sceneId, parentId)
        }
      }
    }
  }

  const processEventsSequentially = async (events: EventResult[], options: ProcessOptions = {}) => {
    store.startProcessing()
    // #791 MR6: task step polling が page unmount で cleanup できるよう AbortController を持つ。
    // resume はスコープ外 (Fable Round 5 見落とし 1)。task は継続実行される、client は完了を待たない。
    const abortController = new AbortController()
    store.setAbortController(abortController)
    try {
      for (let i = 0; i < events.length; i++) {
        if (abortController.signal.aborted) break
        const result = events[i]!
        await processSingleEvent(result, abortController.signal, options)

        // 最後のイベントでは sleep をしない
        if (i < events.length - 1) {
          await sleep(500)
        }
      }
    }
    finally {
      const allSteps = events.flatMap(e => e.steps)
      emitClearEvents(allSteps)
      store.finishProcessing(allSteps)
    }
  }

  const triggerEventAndProcess = async (eventId: string, params?: object, options: ProcessOptions = {}) => {
    const result = await api.triggerEvent(eventId, params)
    await processEventsSequentially(result.results, options)
  }

  // ブラウザ環境で window.__eventSystem を公開（E2Eテスト用）
  if (import.meta.client) {
    window.__eventSystem = {
      isProcessing: computed(() => store.isProcessing),
      lastProcessedSteps: computed(() => store.lastProcessedSteps),
    }
  }

  // #791 MR6: composable が component scope から呼ばれた場合、その component が
  // unmount されたら (route 遷移含む) 進行中の polling を abort する。resume は
  // スコープ外なので、taskId を localStorage に残さず client は結果を諦める
  // (SFN 側は継続実行される、Fable Round 5 見落とし 1)。
  // getCurrentScope が null (script setup 外) の呼び出しでは onScopeDispose は
  // 登録されないので、非 component からの呼び出しでも安全に動く。
  if (typeof getCurrentScope === 'function' && getCurrentScope()) {
    onScopeDispose(() => {
      store.abortActivePolling()
    })
  }

  return {
    isProcessing: computed(() => store.isProcessing),
    lastProcessedSteps: computed(() => store.lastProcessedSteps),
    taskLabel: computed(() => store.taskLabel),
    triggerEventAndProcess,
    processEventsSequentially,
  }
}
